import { useApi } from '@/lib/api/client';
import { startSettingsSync } from '@/lib/settings/sync';
import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import { setReaderIdentity } from '@/lib/preferences/readerPreferences';
import { onAccountReset, sweepEndedAccounts } from '@/lib/auth/accountReset';
import { OidcAppProvider } from '@/lib/auth/Oidc';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { PlatesModeProvider } from '@/features/plates/PlatesMode';

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });
}

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  return (
    <ErrorBoundary>
      <OidcAppProvider>
        <PlatesModeProvider>
          <AuthBoundQueryProvider>{children}</AuthBoundQueryProvider>
        </PlatesModeProvider>
      </OidcAppProvider>
    </ErrorBoundary>
  );
}

export function AuthBoundQueryProvider({ children }: ProvidersProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const sub = user?.profile?.sub;
  // isLoading also flips during every silent refresh; keying on it remounted
  // the whole app and dropped the cache on each renewal of a live session.
  const identity = isAuthenticated
    ? `user:${sub ?? 'unresolved'}`
    : isLoading
      ? 'loading'
      : 'signed-out';

  return (
    <IdentityQueryProvider key={identity} subject={isAuthenticated ? sub : undefined}>
      {children}
    </IdentityQueryProvider>
  );
}

function IdentityQueryProvider({ children, subject }: ProvidersProps & { subject?: string }) {
  const [client] = useState(createQueryClient);
  const api = useApi();

  useLayoutEffect(() => {
    setReaderIdentity(subject);
    const stop = subject ? startSettingsSync(api) : () => {};
    const unregister = onAccountReset(() => { stop(); setReaderIdentity(); });
    return () => { stop(); unregister(); };
  }, [subject, api]);

  useEffect(() => {
    const reset = () => {
      void client.cancelQueries();
      client.clear();
    };
    const stop = onAccountReset(reset);
    return () => {
      stop();
      reset();
      // React runs a removed tree's cleanups parent first; sweeping after the
      // current flush catches what the children write for the ended account as they go.
      queueMicrotask(() => sweepEndedAccounts(subject));
    };
  }, [client, subject]);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
